import { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, Star, Users, Briefcase, ArrowRight } from "lucide-react";
import SeoJsonLd from "@/components/SeoJsonLd";
import {
  absoluteUrl,
  buildBreadcrumbSchema,
  buildFaqSchema,
  buildLocalBusinessSchema,
  buildOrganizationSchema,
  buildPageSchemaGraph,
  buildProductSchemas,
  buildServiceSchema,
} from "@/lib/seoSchemas";

const DEFAULT_OG_IMAGE = "https://getblackride.com/logo.png";
const WHATSAPP_NUMBER = "966540423401";
const WHATSAPP_BASE = `https://wa.me/${WHATSAPP_NUMBER}`;
const DEFAULT_RELATED_LINKS = [
  {
    href: "/chauffeur-service-saudi-arabia",
    title: "Chauffeur Service Saudi Arabia",
    description: "Explore premium chauffeur travel across major Saudi cities and private transfer routes.",
  },
  {
    href: "/chauffeur-service-jeddah",
    title: "Chauffeur Service Jeddah",
    description: "Private chauffeur bookings in Jeddah for hotels, meetings, airport arrivals, and family travel.",
  },
  {
    href: "/airport-transfer-jeddah",
    title: "Airport Transfer Jeddah",
    description: "See how BlackRide handles premium airport pickups, hotel transfers, and arrival coordination.",
  },
  {
    href: "/umrah-chauffeur-service",
    title: "Umrah Chauffeur Service",
    description: "Premium private transport for Umrah travelers moving between the airport, Makkah, Madinah, and hotels.",
  },
];

const FLEET_PREVIEW = [
  {
    name: "BMW 735i",
    category: "First Class",
    image: "/fleet/first-class.png",
    description: "Flagship sedan comfort for executive travel, VIP arrivals, and premium intercity journeys.",
    seats: "3 passengers",
    luggage: "2 bags",
  },
  {
    name: "GMC Yukon XL",
    category: "Elite SUV",
    image: "/fleet/elite-suv.png",
    description: "Spacious premium SUV for airport travelers, families, and guests who want extra luggage capacity.",
    seats: "6-7 passengers",
    luggage: "6 bags",
  },
  {
    name: "Mercedes Vito / Staria",
    category: "Executive Van",
    image: "/fleet/executive.png",
    description: "A practical premium option for airport groups, corporate teams, and larger family transfers.",
    seats: "6 passengers",
    luggage: "4 bags",
  },
  {
    name: "Toyota Hiace",
    category: "Group",
    image: "/fleet/group.png",
    description: "Useful for pilgrim groups, large families, and private transport that needs serious space.",
    seats: "12 passengers",
    luggage: "8 bags",
  },
];

const DEFAULT_REVIEWS = [];

function ensureMeta(selector, attributes) {
  let node = document.querySelector(selector);

  if (!node) {
    node = document.createElement("meta");
    Object.entries(attributes.identity).forEach(([key, value]) => {
      node.setAttribute(key, value);
    });
    document.head.appendChild(node);
  }

  Object.entries(attributes.values).forEach(([key, value]) => {
    node.setAttribute(key, value);
  });
}

function buildWhatsappLink(customMessage, fallbackTitle) {
  const message = encodeURIComponent(
    customMessage ||
      `Hello BlackRide, I would like to book ${fallbackTitle}. Please help me with availability, recommended vehicle class, and a quote.`,
  );
  return `${WHATSAPP_BASE}?text=${message}`;
}

function titleizeCrumb(value) {
  return value.replace(/-/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
}

export default function ServiceLandingPage({
  title,
  metaTitle,
  metaDescription,
  canonical,
  eyebrow,
  intro,
  directAnswer,
  highlightTitle,
  highlights,
  sections,
  faqs,
  primaryCtaLabel = "Book on WhatsApp",
  quoteCtaLabel = "Request a Quote",
  areaServed = "Saudi Arabia",
  ogImage = DEFAULT_OG_IMAGE,
  relatedLinks,
  relatedArticles = [],
  routeLinks = [],
  reviews,
  fleet = FLEET_PREVIEW,
  whatsappMessage,
  quoteWhatsappMessage,
  breadcrumbs,
}) {
  const bookingHref = useMemo(() => buildWhatsappLink(whatsappMessage, title), [title, whatsappMessage]);
  const quoteHref = useMemo(
    () => buildWhatsappLink(quoteWhatsappMessage, `${title} quote`),
    [quoteWhatsappMessage, title],
  );
  const resolvedRelatedLinks = useMemo(() => {
    const links = relatedLinks || DEFAULT_RELATED_LINKS;
    return links.filter((item) => absoluteUrl(item.href) !== canonical);
  }, [canonical, relatedLinks]);
  const resolvedReviews = Array.isArray(reviews) ? reviews : DEFAULT_REVIEWS;
  const breadcrumbItems = useMemo(() => {
    if (breadcrumbs?.length) return breadcrumbs;
    const parts = canonical.replace("https://getblackride.com", "").split("/").filter(Boolean);
    return [{ name: "Home", item: "/" }].concat(
      parts.map((crumb, index) => ({
        name: titleizeCrumb(crumb),
        item: `/${parts.slice(0, index + 1).join("/")}`,
      })),
    );
  }, [breadcrumbs, canonical]);

  const schemaGraph = useMemo(
    () =>
      buildPageSchemaGraph([
        buildOrganizationSchema(),
        buildLocalBusinessSchema({
          description: metaDescription,
          url: canonical,
          areaServed: Array.isArray(areaServed) ? areaServed : [areaServed],
        }),
        buildServiceSchema({
          name: title,
          description: metaDescription,
          url: canonical,
          areaServed: Array.isArray(areaServed) ? areaServed : [areaServed],
          audience: "GCC travellers, Hajj and Umrah pilgrims, airport transfer guests, and premium family travellers",
        }),
        buildBreadcrumbSchema(
          breadcrumbItems.map((item) => ({
            name: item.name,
            item: item.item,
          })),
        ),
        buildFaqSchema(faqs),
        ...buildProductSchemas(fleet, canonical),
      ]),
    [areaServed, breadcrumbItems, canonical, faqs, fleet, metaDescription, title],
  );

  useEffect(() => {
    document.title = metaTitle;

    ensureMeta('meta[name="description"]', {
      identity: { name: "description" },
      values: { content: metaDescription },
    });
    ensureMeta('meta[property="og:title"]', {
      identity: { property: "og:title" },
      values: { content: metaTitle },
    });
    ensureMeta('meta[property="og:description"]', {
      identity: { property: "og:description" },
      values: { content: metaDescription },
    });
    ensureMeta('meta[property="og:url"]', {
      identity: { property: "og:url" },
      values: { content: canonical },
    });
    ensureMeta('meta[property="og:type"]', {
      identity: { property: "og:type" },
      values: { content: "website" },
    });
    ensureMeta('meta[property="og:image"]', {
      identity: { property: "og:image" },
      values: { content: ogImage },
    });
    ensureMeta('meta[name="twitter:card"]', {
      identity: { name: "twitter:card" },
      values: { content: "summary_large_image" },
    });
    ensureMeta('meta[name="twitter:title"]', {
      identity: { name: "twitter:title" },
      values: { content: metaTitle },
    });
    ensureMeta('meta[name="twitter:description"]', {
      identity: { name: "twitter:description" },
      values: { content: metaDescription },
    });
    ensureMeta('meta[name="twitter:image"]', {
      identity: { name: "twitter:image" },
      values: { content: ogImage },
    });

    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement("link");
      canonicalLink.setAttribute("rel", "canonical");
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute("href", canonical);
  }, [canonical, metaDescription, metaTitle, ogImage]);

  return (
    <div className="min-h-screen bg-black text-white pt-24 md:pt-28 service-landing-page">
      <SeoJsonLd id="service-page-schema" data={schemaGraph} />

      <section className="px-6 py-6 md:px-12">
        <div className="max-w-6xl mx-auto flex flex-wrap gap-2 text-xs uppercase tracking-[0.2em] text-white/45">
          {breadcrumbItems.map((item, index) => (
            <span key={item.item} className="inline-flex items-center gap-2">
              {index === breadcrumbItems.length - 1 ? (
                <span className="text-[#C7A239]">{item.name}</span>
              ) : (
                <Link to={item.item} className="hover:text-white transition">
                  {item.name}
                </Link>
              )}
              {index < breadcrumbItems.length - 1 ? <span>/</span> : null}
            </span>
          ))}
        </div>
      </section>

      <section className="px-6 pb-16 md:px-12 md:pb-24 border-b border-white/10">
        <div className="max-w-6xl mx-auto grid gap-10 lg:grid-cols-[1.2fr_0.8fr] items-start">
          <div>
            <p className="text-xs uppercase tracking-[0.28em] text-[#C7A239] font-medium mb-4">{eyebrow}</p>
            <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl lg:text-6xl font-semibold leading-tight mb-6">
              {title}
            </h1>
            <p className="text-gray-300 text-lg leading-8 mb-6 max-w-3xl">{intro}</p>

            {directAnswer ? (
              <div className="rounded-2xl border border-white/10 bg-[#111] p-5 mb-8 max-w-3xl">
                <p className="text-xs uppercase tracking-[0.24em] text-[#C7A239] mb-3">Quick answer</p>
                <p className="text-gray-200 leading-7">{directAnswer}</p>
              </div>
            ) : null}

            <div className="flex gap-4 flex-wrap mb-8">
              <a
                href={bookingHref}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 bg-[#C7A239] text-black px-6 py-3 rounded-full font-medium hover:opacity-90 transition"
              >
                <MessageCircle className="w-4 h-4" />
                {primaryCtaLabel}
              </a>
              <a
                href={quoteHref}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 border border-white/20 text-white px-6 py-3 rounded-full font-medium hover:bg-white hover:text-black transition"
              >
                {quoteCtaLabel}
              </a>
            </div>

            <div className="rounded-2xl border border-[#C7A239]/25 bg-[#101010] p-5 max-w-3xl">
              <p className="text-xs uppercase tracking-[0.24em] text-[#C7A239] mb-3">Pricing & availability</p>
              <p className="text-gray-300 leading-7">
                Final pricing depends on route, timing, vehicle class, and service notes. Send your trip details on
                WhatsApp and BlackRide will confirm availability, recommend the right vehicle, and share a tailored
                quote.
              </p>
            </div>
          </div>

          <div className="bg-[#111] border border-white/10 rounded-3xl p-6 lg:sticky lg:top-28">
            <h2 className="text-2xl font-semibold text-white mb-4">{highlightTitle}</h2>
            <ul className="space-y-4 text-gray-300 leading-7">
              {highlights.map((item) => (
                <li key={item} className="flex gap-3">
                  <span className="mt-2 h-2 w-2 rounded-full bg-[#C7A239] shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="px-6 py-16 md:px-12">
        <div className="max-w-5xl mx-auto space-y-10 text-gray-300 leading-8">
          {sections.map((section) => (
            <div key={section.title}>
              <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-white mb-4">{section.title}</h2>
              <div className="space-y-5">
                {section.paragraphs.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {routeLinks.length ? (
        <section className="px-6 py-16 md:px-12 bg-[#0d0d0d] border-y border-white/10">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-end justify-between gap-6 flex-wrap mb-8">
              <div>
                <p className="text-xs uppercase tracking-[0.25em] text-[#C7A239] font-medium mb-3">Popular routes</p>
                <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-white">
                  Related Saudi transfer routes and travel hubs
                </h2>
              </div>
              <a href={bookingHref} target="_blank" rel="noreferrer" className="text-[#C7A239] hover:text-[#D4B34A] transition font-medium">
                Ask about route combinations
              </a>
            </div>

            <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
              {routeLinks.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className="rounded-2xl border border-white/10 bg-[#111] p-6 hover:border-[#C7A239]/40 transition"
                >
                  <p className="text-xs uppercase tracking-[0.22em] text-[#C7A239] mb-3">{item.anchorText}</p>
                  <h3 className="text-xl font-semibold text-white mb-2">{item.title}</h3>
                  <p className="text-gray-300 leading-7 mb-4">{item.description}</p>
                  <span className="inline-flex items-center gap-2 text-[#C7A239] font-medium">
                    Explore route
                    <ArrowRight className="w-4 h-4" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      <section className="px-6 py-16 md:px-12 bg-[#0b0b0b] border-y border-white/10">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-end justify-between gap-6 flex-wrap mb-8">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-[#C7A239] font-medium mb-3">Fleet</p>
              <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-white">
                Vehicle classes suitable for this journey
              </h2>
            </div>
            <a href={bookingHref} target="_blank" rel="noreferrer" className="text-[#C7A239] hover:text-[#D4B34A] transition font-medium">
              Discuss fleet options on WhatsApp
            </a>
          </div>

          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
            {fleet.map((item) => (
              <article key={item.name} className="rounded-3xl border border-white/10 bg-[#111] overflow-hidden">
                <div className="h-48 bg-[#0d0d0d] overflow-hidden">
                  <img
                    src={item.image}
                    alt={`${item.name} premium chauffeur vehicle for ${title}`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    width="640"
                    height="420"
                  />
                </div>
                <div className="p-5">
                  <p className="text-xs uppercase tracking-[0.24em] text-[#C7A239] mb-2">{item.category}</p>
                  <h3 className="text-xl font-semibold text-white mb-2">{item.name}</h3>
                  <p className="text-gray-300 leading-6 mb-4">{item.description}</p>
                  <div className="flex flex-wrap gap-4 text-xs text-gray-400">
                    <span className="inline-flex items-center gap-2">
                      <Users className="w-3.5 h-3.5 text-[#C7A239]" />
                      {item.seats}
                    </span>
                    <span className="inline-flex items-center gap-2">
                      <Briefcase className="w-3.5 h-3.5 text-[#C7A239]" />
                      {item.luggage}
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {resolvedReviews.length ? (
        <section className="px-6 py-16 md:px-12">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10">
              <p className="text-xs uppercase tracking-[0.25em] text-[#C7A239] font-medium mb-3">Reviews</p>
              <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-white">What travelers say about BlackRide</h2>
            </div>
            <div className="grid gap-5 md:grid-cols-3">
              {resolvedReviews.map((review) => (
                <article key={review.name} className="rounded-2xl border border-white/10 bg-[#111] p-6">
                  <div className="flex gap-1 mb-4">
                    {Array.from({ length: 5 }).map((_, index) => (
                      <Star key={index} className="w-4 h-4 fill-[#C7A239] text-[#C7A239]" />
                    ))}
                  </div>
                  <p className="text-gray-300 leading-7 mb-4">“{review.text}”</p>
                  <p className="text-white font-medium">{review.name}</p>
                </article>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      <section className="px-6 py-16 md:px-12 bg-[#0b0b0b] border-y border-white/10">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-white mb-8">Frequently Asked Questions</h2>
          <div className="space-y-6 text-gray-300 leading-8">
            {faqs.map((faq) => (
              <div key={faq.question}>
                <h3 className="text-xl font-semibold text-white mb-2">{faq.question}</h3>
                <p>{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 py-16 md:px-12 border-b border-white/10">
        <div className="max-w-6xl mx-auto space-y-12">
          <div>
            <div className="flex items-end justify-between gap-6 flex-wrap mb-8">
              <div>
                <p className="text-xs uppercase tracking-[0.25em] text-[#C7A239] font-medium mb-3">Explore More</p>
                <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-white">
                  Related chauffeur and transfer pages
                </h2>
              </div>
              <Link to="/blog" className="text-[#C7A239] hover:text-[#D4B34A] transition font-medium">
                Explore all BlackRide guides
              </Link>
            </div>

            <div className="grid gap-5 md:grid-cols-2">
              {resolvedRelatedLinks.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className="rounded-2xl border border-white/10 bg-[#111] p-6 hover:border-[#C7A239]/40 transition"
                >
                  <h3 className="text-xl font-semibold text-white mb-2">{item.title}</h3>
                  <p className="text-gray-300 leading-7">{item.description}</p>
                </Link>
              ))}
            </div>
          </div>

          {relatedArticles.length ? (
            <div>
              <div className="flex items-end justify-between gap-6 flex-wrap mb-8">
                <div>
                  <p className="text-xs uppercase tracking-[0.25em] text-[#C7A239] font-medium mb-3">Related guides</p>
                  <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-white">
                    Articles that support this route or service
                  </h2>
                </div>
              </div>

              <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
                {relatedArticles.map((item) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    className="rounded-2xl border border-white/10 bg-[#111] p-6 hover:border-[#C7A239]/40 transition"
                  >
                    <h3 className="text-xl font-semibold text-white mb-2">{item.title}</h3>
                    <p className="text-gray-300 leading-7">{item.description}</p>
                  </Link>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </section>

      <section className="px-6 py-16 md:px-12">
        <div className="max-w-5xl mx-auto rounded-3xl border border-[#C7A239]/25 bg-[linear-gradient(135deg,#111111,#171717)] p-8 md:p-12 text-center">
          <p className="text-xs uppercase tracking-[0.28em] text-[#C7A239] font-medium mb-4">Ready to book?</p>
          <h2 className="font-['Playfair_Display'] text-3xl md:text-5xl text-white mb-4">
            Share your trip and receive a premium chauffeur quote
          </h2>
          <p className="text-gray-300 text-lg leading-8 max-w-3xl mx-auto mb-8">
            Send your route, date, time, passenger count, luggage details, and preferred vehicle class on WhatsApp.
            BlackRide will confirm availability and guide you to the right booking option.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <a
              href={bookingHref}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 bg-[#C7A239] text-black px-6 py-3 rounded-full font-medium hover:opacity-90 transition"
            >
              <MessageCircle className="w-4 h-4" />
              {primaryCtaLabel}
            </a>
            <a
              href={quoteHref}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 border border-white/20 text-white px-6 py-3 rounded-full font-medium hover:bg-white hover:text-black transition"
            >
              {quoteCtaLabel}
            </a>
          </div>
        </div>
      </section>

      <div className="fixed bottom-4 left-4 right-4 z-30 md:hidden">
        <a
          href={bookingHref}
          target="_blank"
          rel="noreferrer"
          className="w-full inline-flex items-center justify-center gap-2 bg-[#C7A239] text-black px-5 py-3 rounded-full font-semibold shadow-[0_18px_38px_rgba(0,0,0,0.35)]"
        >
          <MessageCircle className="w-4 h-4" />
          {primaryCtaLabel}
        </a>
      </div>
    </div>
  );
}
